import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";
import { setCurrentPage, setSearchText } from "../../../../redux/historicalSlice";
import { Button, Col, Form, Row } from "react-bootstrap";
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import AlertMessage from "../../../../components/AlertMessage";
import Search from "../../../../components/Search";
import { SelectedFilter } from "../../../../components/Filters/types";
import AppTable, { ColumnConfig } from "../../../../components/AppTable/AppTable";
import { SectionPermissionTypeEnum } from "../../../../types/SectionPermissionTypesEnum";
import { useAlert } from "../../../../hooks/useAlert";
import ModalGlobal from "../../../../components/AppModal/ModalGlobal";
import { useModal } from "../../../../hooks/useModal";
import { IPsListItemDto } from "../types";
import { useSecurityIPS } from "../hook/useSecurityIPS";
import { setFilterByOnlyActiveRecords } from "../../../../redux/filtersSlice";
import { activeSecurityIPS, deleteSecurityIPS } from "../api/securityIPSAPI";

export default function SecurityIPsList() {

    const { t } = useTranslation(["common","securityIPs"]);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const currentPage = useSelector((state: RootState) => state.historical.ips.currentPage);
    const itemsPerPage = useSelector((state: RootState) => state.historical.ips.itemsPerPage);
    const searchText = useSelector((state: RootState) => state.historical.ips.searchText);

    const [searchTerm, setSearchTerm] = useState("");
    const [appliedFilters] = useState<SelectedFilter[]>([]);

    const { data: ipsPagination, isLoading, error, refetch } = useSecurityIPS({search: searchTerm, filters: appliedFilters,});

    useEffect(() => {
        if (location.state?.shouldRefresh) {
            refetch();
        }
    }, [location.state, refetch]);

    const handleClear = () => {
        dispatch(setCurrentPage({ table: "ips", page: 1 }));
        dispatch(setSearchText({ table: "ips", text: '' }));
        setSearchTerm("");
    }

    const handleSearchInput = () => {
        setSearchTerm(searchText || '');
        dispatch(setCurrentPage({ table: "ips", page: 1 }));
    };

 
    const { hideAlert, alert, isVisible } = useAlert({ fromLocation: true });

    const columns: ColumnConfig<IPsListItemDto>[] = [
        { header: t("securityIPs:headerTableSecurityIPsId"), key: "id", render: (item) => <b>{item.id}</b> },
        { header: t("securityIPs:headerTableSecurityIPsIPOrigin"), key: "ipOrigin" },
       { header: t("securityIPs:headerTableSecurityIPsIPDestiny"), key: "ipDestiny" },
        { header: t("securityIPs:headerTableSecurityIPsIPIsActive"), key: "isActive", render: (item) => item.isActive ? t("securityIPs:stateActive") : t("securityIPs:stateInactive") },    ];

    const { showModal, modalData, openModal, closeModal,errorModal,setErrorModal,currentId} = useModal<IPsListItemDto>();

    const confirmSubmitModal = async () => {

        try {
            if (modalData.type === 'deleteWithoutInput') {

                await deleteSecurityIPS(currentId);
                navigate('/admin/event', {
                    state: { message: t("securityIPs:modal.securityIPDeleteSuccessMissage"), shouldRefresh: true }
                });
            }
            else if(modalData.type === 'active'){
                await activeSecurityIPS(currentId);
                navigate('/admin/event', {
                    state: { message: t("securityIPs:modal.securityIPDeleteSuccessMissage"), shouldRefresh: true }
                });
            }
            closeModal();
        } catch (error) {
            setErrorModal({ errorModal: t("common:requestError.errorBadRequest") });
            console.log(error);
        }
    }
    const handleToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(setFilterByOnlyActiveRecords({ table: "ips", filterByOnlyActiveRecords: e.target.checked }));
    };

    const onlyActiveToggle = useSelector((state: RootState) => state.filters.ips.filterByOnlyActiveRecords);
    
    const handleActive = async (ip: IPsListItemDto) => {
        openModal(
            ip,
            t("securityIPs:modal.modalRestoreSecurityIPsTitle"),
            t("securityIPs:modal.modalRestoreSecurityIPsSubtitle") + `${ip.ipOrigin} ${ip.ipDestiny}?`,
            "active",
            ""
        );
    };
    return (
        <>
            <ModalGlobal
                show={showModal}
                onClose={closeModal}
                title={modalData.title}
                contentType={modalData.type}
                subtitle={modalData.subtitle}
                saveText={t("common:modalActionOK")}
                error={errorModal?.endDate || errorModal?.errorModal}
                onSave={confirmSubmitModal}
                isLoading={isLoading}
            />
            <Row className="mb-2 align-items-center justify-content-between">
                <Col xs={12} md>
                    <h2>{t("securityIPs:titleSecurityIPS")}</h2>
                </Col>
                <Col xs={12} md="auto" className="d-flex align-items-center justify-content-end mt-2 mt-md-0">
                    <Button
                        variant="primary"
                        style={{ whiteSpace: "nowrap" }}
                        onClick={() => navigate(`/admin/ips/new-ips`)}>
                        {t("securityIPs:newSecurityIPS")}
                    </Button>
                </Col>
            </Row>
            <Row>
                <Col className="text-center" xs={12} md={12}>
                    {isVisible && (
                        <AlertMessage
                            message={alert!.message}
                            type={alert!.type}
                            onClose={hideAlert}
                        />
                    )}
                </Col>
                <Col className="text-center" xs={12} md={12}>
                    {error && (
                        <div className="alert alert-danger">{t("common:requestError.errorBadRequest")}</div>
                    )}
                </Col>
            </Row>
            <Row className="mb-2">
                <Col xs={12}>
                    <Search
                        searchText={searchText || ''}
                        onChange={(text) => dispatch(setSearchText({ table: "ips", text: text }))}
                        onSearch={handleSearchInput}
                        onClear={handleClear}
                    />
                </Col>
            </Row>
            <Row>
                <Form>
                    <Form.Check
                        className="my-4"
                        type="switch"
                        id="custom-switch"
                        label={t("common:buttons.toggleActiveRecords")}
                        onChange={handleToggle}
                        checked={onlyActiveToggle}
                    />
                </Form>
            </Row>
            <AppTable<IPsListItemDto>
                data={ipsPagination?.items || []}
                columns={columns}
                emptyMessage={"No records found"}
                t={t}
                hasActions={true}
                hasPagination={true}
                currentPage={currentPage}
                totalPages={ipsPagination?.totalPages || 0}
                pageSize={itemsPerPage}
                paginationType="ips"
                onExportCsv={() => { }}
                onDelete={(item) => { openModal(item, t("securityIPs:modal.modalDeleteSecurityIPTitle"), `${t("securityIPs:modal.modalDeleteSecurityIPSubtitle")} ${item.ipOrigin}?`, "deleteWithoutInput", "") }}
                onView={(id: number) => { navigate(`/admin/ips/${id}`) }}
                onActive={handleActive}
                deleteCondition={(item) => !!item.isActive}
                activeCondition={(item) => !item.isActive}
                sectionPermission={SectionPermissionTypeEnum.EVENTS}
            />
        </>
    );
}
